// The lobby a phone is waiting in, kept alive while the player browses other pages.
// When the host starts the game, the player is warned and taken to the controller.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { SessionStatus } from '@shared/protocol';
import type { StoredSeat } from '../pages/mobile/JoinPage';
import { vibrate } from './device';
import { keys, storage } from './storage';
import { useGameSocket } from './useGameSocket';

const CHANGED = 'masa:active-room';
/** Seconds the "game is starting" warning counts down before switching to the controller. */
const START_COUNTDOWN = 3;

export interface ActiveLobby {
  code: string;
  gameTitle: string | null;
  status: SessionStatus | null;
  players: number;
  connected: boolean;
}

interface Ctx {
  lobby: ActiveLobby | null;
  /** Stops waiting in the lobby on this device (the seat stays with the host until it is cleared there). */
  leave(): void;
}

const LobbyContext = createContext<Ctx>({ lobby: null, leave: () => {} });

/** Marks `code` as the lobby this device waits in. */
export function setActiveRoom(code: string): void {
  storage.set(keys.activeRoom, code.toUpperCase());
  window.dispatchEvent(new Event(CHANGED));
}

/** Forgets the active lobby (the room ended, we were removed, or we left). */
export function clearActiveRoom(code?: string): void {
  if (code && storage.get(keys.activeRoom) !== code.toUpperCase()) return;
  storage.remove(keys.activeRoom);
  window.dispatchEvent(new Event(CHANGED));
}

function readActive(): { code: string; seat: StoredSeat } | null {
  const code = storage.get(keys.activeRoom);
  const seat = code ? storage.getJson<StoredSeat>(keys.player(code)) : null;
  return code && seat ? { code, seat } : null;
}

export function useActiveLobby(): Ctx {
  return useContext(LobbyContext);
}

/**
 * Keeps the seat's connection open while the player is anywhere but the controller page (which has
 * its own), so the host still sees them as connected and friends see them in the room.
 */
export function ActiveLobbyProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState(readActive);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onController = !!active && pathname.toUpperCase() === `/PLAY/${active.code}`;
  // TV/host screens are a different role; do not hold a phone seat open there.
  const onHostScreen = pathname.startsWith('/tv/session/') || pathname.startsWith('/tv/ekran/');
  const sock = useGameSocket(active && !onController && !onHostScreen ? active.seat.token : null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const lastStatus = useRef<SessionStatus | null>(null);

  useEffect(() => {
    const refresh = () => setActive(readActive());
    window.addEventListener(CHANGED, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(CHANGED, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // The room is gone or we were removed: stop showing it.
  useEffect(() => {
    if (!active || onController) return;
    if (sock.status === 'kicked' || sock.status === 'invalid' || sock.status === 'closed') {
      if (sock.status !== 'closed') storage.remove(keys.player(active.code));
      clearActiveRoom(active.code);
    }
  }, [sock.status, active, onController]);

  const status = sock.state?.session.status ?? null;
  // Lobby (or results) → playing while browsing: warn, then take the player into the game.
  useEffect(() => {
    const before = lastStatus.current;
    lastStatus.current = status;
    if (!active || onController || status !== 'playing' || before === 'playing') return;
    vibrate([120, 60, 120]);
    setCountdown(START_COUNTDOWN);
  }, [status, active, onController]);

  const join = useCallback(() => {
    if (!active) return;
    setCountdown(null);
    navigate(`/play/${active.code}`);
  }, [active, navigate]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) return join();
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown, join]);

  useEffect(() => {
    if (onController) setCountdown(null);
  }, [onController]);

  const leave = useCallback(() => {
    if (!active) return;
    storage.remove(keys.player(active.code));
    clearActiveRoom(active.code);
  }, [active]);

  const session = sock.state?.session;
  const lobby: ActiveLobby | null = active
    ? {
        code: active.code,
        gameTitle: session?.game.title ?? null,
        status: session?.status ?? null,
        players: session?.players.length ?? 0,
        connected: onController || sock.status === 'connected',
      }
    : null;

  return (
    <LobbyContext.Provider value={{ lobby, leave }}>
      {children}
      {countdown !== null && active && (
        <div className="lobby-start" role="alertdialog" aria-live="assertive" aria-labelledby="lobby-start-title">
          <div className="card lobby-start-card anim-pop">
            <div className="lobby-start-count">{Math.max(0, countdown)}</div>
            <h2 id="lobby-start-title">Oyun başlıyor!</h2>
            <p className="muted">{session?.game.title ?? 'Oyun'} başlatıldı, seni oyuna götürüyoruz…</p>
            <button className="btn primary lg block" onClick={join} autoFocus>
              Hemen katıl
            </button>
          </div>
        </div>
      )}
    </LobbyContext.Provider>
  );
}

const STATUS_TEXT: Record<SessionStatus, string> = { lobby: 'Lobide', playing: 'Oyun başladı', ended: 'Yeni tur bekleniyor' };

/** Top-bar chip for the lobby this device is waiting in; hidden on the controller itself. */
export function LobbyChip() {
  const { lobby, leave } = useActiveLobby();
  const { pathname } = useLocation();
  const [confirm, setConfirm] = useState(false);
  if (!lobby || pathname.toUpperCase() === `/PLAY/${lobby.code}`) return null;
  return (
    <div className={`lobby-chip ${lobby.status === 'playing' ? 'st-playing' : 'st-lobby'}`}>
      <Link to={`/play/${lobby.code}`} className="lobby-chip-main" title="Lobiye dön">
        <span className={`fc-dot ${lobby.connected ? (lobby.status === 'playing' ? 'st-playing' : 'st-lobby') : ''}`} />
        <span className="lobby-chip-text">
          <b>{lobby.status ? STATUS_TEXT[lobby.status] : 'Lobi'}</b>
          <span className="hide-narrow">
            {' '}
            · {lobby.gameTitle ?? lobby.code}
            {lobby.players > 0 && ` · ${lobby.players} oyuncu`}
          </span>
        </span>
        <span className="lobby-chip-code">{lobby.code}</span>
      </Link>
      {confirm ? (
        <>
          <button className="btn sm danger" onClick={leave}>
            Ayrıl
          </button>
          <button className="btn sm ghost" onClick={() => setConfirm(false)}>
            Vazgeç
          </button>
        </>
      ) : (
        <button className="btn sm ghost icon" onClick={() => setConfirm(true)} aria-label="Lobiden ayrıl" title="Lobiden ayrıl">
          ✕
        </button>
      )}
    </div>
  );
}
